import {
    XIcon as CentralXIcon,
    ChevronSmallLeftIcon as CentralChevronSmallLeftIcon,
    ChevronSmallRightIcon as CentralChevronSmallRightIcon,
} from "./product-icons.tsx";
import { VideoWithFallback } from "./video-with-fallback.tsx";
import { edgeScrimRef } from "./browser-edges.tsx";
import { edgeScrim } from "./edge-color.ts";
import { lockDocument } from "./lock-document.ts";
import { isVideoMedia } from "@wags/utils/media.ts";
import {
    type ReactNode,
    useEffect,
    useEffectEvent,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { MediaThumbnail, MediaThumbnailButton } from "./media-thumbnail.tsx";
import { ImageErrorFallback, VercelImage } from "./vercel-image.js";

type MediaItem = { url: string; mimeType?: string | null };

function isPointOnContainedImage(
    x: number,
    y: number,
    img: HTMLImageElement,
): boolean {
    const { naturalWidth: nw, naturalHeight: nh } = img;

    if (!nw || !nh) return true;
    const r = img.getBoundingClientRect();
    const scale = Math.min(r.width / nw, r.height / nh);
    const cw = nw * scale;
    const ch = nh * scale;
    const cx = r.left + (r.width - cw) / 2;
    const cy = r.top + (r.height - ch) / 2;
    return x >= cx && x <= cx + cw && y >= cy && y <= cy + ch;
}

interface ImageCarouselDialogProps {
    mediaItems: MediaItem[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    alt?: string;
    initialIndex?: number;
    onIndexChange?: (index: number) => void;
    listingMedia?: boolean;
    loadingPlaceholder?: ReactNode;
}

export function ImageCarouselDialog({
    mediaItems,
    open,
    onOpenChange,
    alt = "Media",
    initialIndex = 0,
    onIndexChange,
    listingMedia = false,
    loadingPlaceholder,
}: ImageCarouselDialogProps) {
    if (!open) return null;
    return (
        <Lightbox
            mediaItems={mediaItems}
            alt={alt}
            initialIndex={initialIndex}
            onClose={() => onOpenChange(false)}
            onIndexChange={onIndexChange}
            listingMedia={listingMedia}
            loadingPlaceholder={loadingPlaceholder}
        />
    );
}

const LIGHTBOX_SCRIM = edgeScrim("bg-true-black");
const LIGHTBOX_EDGE_REF = edgeScrimRef(LIGHTBOX_SCRIM.tint);

function Lightbox({
    mediaItems,
    alt,
    initialIndex,
    onClose,
    onIndexChange,
    listingMedia,
    loadingPlaceholder,
}: {
    mediaItems: MediaItem[];
    alt: string;
    initialIndex: number;
    onClose: () => void;
    onIndexChange?: (index: number) => void;
    listingMedia: boolean;
    loadingPlaceholder?: ReactNode;
}) {
    const count = mediaItems.length;
    const [openingIndex] = useState(initialIndex);
    const [index, setIndex] = useState(openingIndex);
    const selectedIndex = useRef(openingIndex);
    const trackRef = useRef<HTMLDivElement>(null);
    const thumbStripRef = useRef<HTMLDivElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const [attachRoot] = useState(() => (element: HTMLDivElement | null) => {
        rootRef.current = element;
        return LIGHTBOX_EDGE_REF?.(element);
    });
    const scrollFrame = useRef<number | null>(null);
    const [fadeFrom, setFadeFrom] = useState<number | null>(null);
    const outgoingMedia = fadeFrom === null ? undefined : mediaItems[fadeFrom];

    const selectIndex = (next: number) => {
        if (next === selectedIndex.current) return;
        selectedIndex.current = next;
        setIndex(next);
        onIndexChange?.(next);
    };

    const scrollToIndex = (i: number, transition: "crossfade" | "instant") => {
        const el = trackRef.current;
        if (!el) return;
        const clamped = Math.max(0, Math.min(count - 1, i));

        if (clamped === index) return;
        const crossfade =
            transition === "crossfade" &&
            !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        setFadeFrom(crossfade ? index : null);
        selectIndex(clamped);
        el.scrollTo({ left: clamped * el.clientWidth, behavior: "instant" });
    };

    const onKey = useEffectEvent((e: KeyboardEvent) => {
        if (e.key === "Escape") onClose();
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        e.preventDefault();
        scrollToIndex(index + (e.key === "ArrowRight" ? 1 : -1), "instant");
    });

    useLayoutEffect(() => {
        const el = trackRef.current;
        if (el) {
            el.scrollTo({
                left: openingIndex * el.clientWidth,
                behavior: "instant",
            });
        }
    }, [openingIndex]);

    useEffect(() => {
        const dialog = rootRef.current;
        const unlock = dialog ? lockDocument(dialog) : null;
        dialog?.focus();

        window.addEventListener("keydown", onKey);

        return () => {
            unlock?.();
            window.removeEventListener("keydown", onKey);
            if (scrollFrame.current !== null) {
                cancelAnimationFrame(scrollFrame.current);
                scrollFrame.current = null;
            }
        };
    }, []);

    const onScroll = () => {
        if (scrollFrame.current !== null) return;
        scrollFrame.current = requestAnimationFrame(() => {
            scrollFrame.current = null;
            const el = trackRef.current;
            if (!el || el.clientWidth === 0) return;
            selectIndex(
                Math.max(
                    0,
                    Math.min(
                        count - 1,
                        Math.round(el.scrollLeft / el.clientWidth),
                    ),
                ),
            );
        });
    };

    useEffect(() => {
        (
            thumbStripRef.current?.children[index] as HTMLElement | undefined
        )?.scrollIntoView({ inline: "center", block: "nearest" });
    }, [index]);

    const controlClass =
        "flex size-8 items-center justify-center rounded-full after:absolute after:-inset-1.5 border border-true-white/15 bg-true-white/15 text-true-white shadow-sm backdrop-blur-md transition-colors hover:bg-true-white/25 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-true-white cursor-pointer";
    const arrowClass = `absolute top-1/2 z-10 hidden -translate-y-1/2 md:flex ${controlClass}`;

    return createPortal(
        // oxlint-disable-next-line jsx-a11y/click-events-have-key-events -- tap-to-close is a pointer-only affordance; keyboard users close via the window-level Escape handler above.
        <div
            ref={attachRoot}
            role="dialog"
            aria-modal="true"
            aria-label={`${alt} gallery`}
            tabIndex={-1}
            data-slot="dialog-overlay"
            onClick={(e) => {
                const target = e.target as HTMLElement;
                if (target.closest("button, a, video, [data-lightbox-chrome]"))
                    return;
                const img = target.closest("img");
                if (
                    img &&
                    isPointOnContainedImage(
                        e.clientX,
                        e.clientY,
                        img as HTMLImageElement,
                    )
                )
                    return;
                onClose();
            }}

            className={`fixed inset-0 z-[60] flex flex-col ${LIGHTBOX_SCRIM.className} outline-none animate-in fade-in-0 duration-150 ease-out`}
        >
            <div
                data-lightbox-chrome
                className="flex items-center justify-between px-6 pt-[calc(env(safe-area-inset-top)+24px)] pb-4 text-true-white"
            >
                {count > 1 ? (
                    <span
                        className="text-caption tabular-nums"
                        aria-live="polite"
                    >
                        <span aria-hidden="true">
                            {index + 1} / {count}
                        </span>
                        <span className="sr-only">
                            Media {index + 1} of {count}
                        </span>
                    </span>
                ) : (
                    <span />
                )}
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close gallery"
                    className={`relative ${controlClass}`}
                >
                    <CentralXIcon className="size-5" aria-hidden="true" />
                </button>
            </div>

            <div className="relative flex-1 min-h-0">
                <div
                    ref={trackRef}
                    onScroll={onScroll}
                    onScrollEnd={onScroll}
                    onPointerDown={() => setFadeFrom(null)}
                    onWheel={() => setFadeFrom(null)}
                    className="flex h-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden scroll-smooth motion-reduce:scroll-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                    {mediaItems.map((m, i) => (
                        <div
                            key={`${i}-${m.url}`}
                            className="relative flex h-full w-full flex-none snap-center items-center justify-center px-4 md:px-24"
                        >
                            {isVideoMedia(m) ? (
                                i === index ? (
                                    <VideoWithFallback
                                        fallback={
                                            <div
                                                data-lightbox-chrome
                                                className="h-full w-full"
                                            >
                                                <ImageErrorFallback
                                                    alt={`${alt} ${i + 1}`}
                                                    className="h-full w-full"
                                                />
                                            </div>
                                        }
                                        src={m.url}
                                        aria-label={`${alt} ${i + 1}`}
                                        controls
                                        muted
                                        autoPlay
                                        loop
                                        playsInline
                                        className="max-h-full max-w-full object-contain"
                                    />
                                ) : (
                                    <MediaThumbnail
                                        media={m}
                                        alt={`${alt} ${i + 1}`}
                                        listingMedia={listingMedia}
                                        width={1200}
                                        height={800}
                                        className="h-full w-full"
                                        mediaClassName="object-contain"
                                    />
                                )
                            ) : (
                                <VercelImage
                                    src={m.url}
                                    listingMedia={listingMedia}
                                    mimeType={m.mimeType}
                                    alt={`${alt} ${i + 1}`}
                                    width={1200}
                                    height={800}
                                    priority={i === openingIndex}
                                    loadingPlaceholder={
                                        i === index
                                            ? loadingPlaceholder
                                            : undefined
                                    }

                                    loading={
                                        Math.abs(i - openingIndex) <= 1
                                            ? "eager"
                                            : "lazy"
                                    }
                                    draggable={false}
                                    className="h-full w-full object-contain"
                                />
                            )}
                        </div>
                    ))}
                </div>

                {outgoingMedia && (
                    <div
                        key={index}
                        aria-hidden="true"
                        data-slot="media-crossfade"
                        onAnimationEnd={() => setFadeFrom(null)}
                        className="pointer-events-none absolute inset-0 flex items-center justify-center px-4 md:px-24 animate-out fade-out-0 duration-200 ease-out [animation-fill-mode:forwards] motion-reduce:hidden"
                    >
                        <VercelImage
                            src={outgoingMedia.url}
                            listingMedia={listingMedia}
                            mimeType={outgoingMedia.mimeType}
                            alt=""
                            width={1200}
                            height={800}
                            videoMode="thumbnail"
                            draggable={false}
                            className="h-full w-full object-contain"
                        />
                    </div>
                )}

                {count > 1 && index > 0 && (
                    <button
                        type="button"
                        className={`${arrowClass} left-6`}
                        onClick={() => scrollToIndex(index - 1, "crossfade")}
                        aria-label="Previous media"
                    >
                        <CentralChevronSmallLeftIcon
                            className="size-5"
                            aria-hidden="true"
                        />
                    </button>
                )}
                {count > 1 && index < count - 1 && (
                    <button
                        type="button"
                        className={`${arrowClass} right-6`}
                        onClick={() => scrollToIndex(index + 1, "crossfade")}
                        aria-label="Next media"
                    >
                        <CentralChevronSmallRightIcon
                            className="size-5"
                            aria-hidden="true"
                        />
                    </button>
                )}
            </div>

            {count > 1 && (
                <div
                    data-lightbox-chrome
                    className="flex justify-center pt-4 pb-[calc(env(safe-area-inset-bottom)+20px)]"
                >
                    <div
                        ref={thumbStripRef}
                        className="flex max-w-[92vw] gap-3 overflow-x-auto px-2 py-1 scroll-smooth motion-reduce:scroll-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    >
                        {mediaItems.map((m, i) => (
                            <MediaThumbnailButton
                                key={`${i}-${m.url}`}
                                media={m}
                                listingMedia={listingMedia}
                                label={`View ${alt} ${isVideoMedia(m) ? "video" : "image"} ${i + 1}`}
                                selected={i === index}
                                onClick={() => scrollToIndex(i, "crossfade")}
                                size="md"
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>,
        document.body,
    );
}
